import {
  HttpError,
  ScheduleChangeResultSchema,
  type ScheduleChangeResult,
} from '@voll-intelligence/domain';
import { env } from '@web/core';
import type { AuthService } from '@web/data/services';
import { trimTrailingSlashes } from '@web/data/services/url';

export interface ScheduleChangeRepository {
  /**
   * Upload a schedule-change file to the kanban-api bridge for parsing.
   * `orgId` is the resolved 24-char hex ObjectId (the route validates
   * `/^[a-f\d]{24}$/i`), NOT the URL slug.
   */
  submitUpload: (orgId: string, processorType: string, file: File) => Promise<ScheduleChangeResult>;
}

/**
 * Schedule-change upload client. Owns its own `fetch` (D-P4-02): the file is
 * multipart `FormData`, which must NOT route through `HttpService` (that helper
 * sets `Content-Type: application/json`). The browser sets the multipart
 * `Content-Type` boundary itself, so this never sets that header.
 *
 * On a non-ok response it throws `HttpError(message, status, payload)` so the
 * view can read `details.reason` for the whole-file error banner; on success it
 * validates the body against the canonical `ScheduleChangeResultSchema`.
 */
export function createScheduleChangeRepository(
  authService: Pick<AuthService, 'getAccessToken'>,
): ScheduleChangeRepository {
  const base = trimTrailingSlashes(env.VITE_KANBAN_API_URL);

  return {
    submitUpload: async (orgId, processorType, file) => {
      const form = new FormData();
      form.append('processorType', processorType);
      form.append('file', file);

      const token = await authService.getAccessToken();
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${base}/orgs/${orgId}/schedule-changes/parse`, {
        method: 'POST',
        headers,
        body: form,
        credentials: 'omit',
      });

      const payload: unknown = await res.json().catch(() => undefined);

      if (!res.ok) {
        const details =
          payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : undefined;
        const message =
          (details && typeof details.error === 'string' && details.error) ||
          `HTTP ${String(res.status)}`;
        throw new HttpError(message, res.status, details);
      }

      return ScheduleChangeResultSchema.parse(payload);
    },
  };
}
